import { useState } from "react";
import { Descriptions, Input, Button, message } from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { IPropertySale } from "types/PropertyTypes";
import { calcBuildingAge } from "../utils/simulationCalc";
import { buildDrivePreviewUrl } from "../utils/driveUrl";

const { TextArea } = Input;

interface PropertySaleDetailsContentProps {
  propertySale: IPropertySale | undefined;
  loading?: boolean;
  goodReason?: string | null;
  badReason?: string | null;
  simulationId?: number | null;
  onReasonsUpdate?: (simulationId: number, goodReason: string, badReason: string) => Promise<void>;
}

const formatJapaneseDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  let date: Date;

  // Kiểm tra nếu là format YYYYMMDD (8 ký tự số)
  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(dateString.substring(6, 8));
    date = new Date(year, month, day);
  } else {
    // Thử parse như date string bình thường
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return dateString; // Return original if can't parse

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}年${month}月${day}日`;
};

export default function PropertySaleDetailsContent({
  propertySale,
  loading = false,
  goodReason,
  badReason,
  simulationId,
  onReasonsUpdate,
}: PropertySaleDetailsContentProps) {
  const [editing, setEditing] = useState(false);
  const [editGood, setEditGood] = useState('');
  const [editBad, setEditBad] = useState('');
  const [saving, setSaving] = useState(false);

  const handleStartEdit = () => {
    setEditGood(goodReason || '');
    setEditBad(badReason || '');
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    if (!simulationId || !onReasonsUpdate) return;
    setSaving(true);
    try {
      await onReasonsUpdate(simulationId, editGood, editBad);
      setEditing(false);
      message.success('査定ポイントを保存しました');
    } catch {
      message.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ color: '#6B7280' }}>読み込み中...</div>;

  if (!loading && !propertySale) {
    return <h3 style={{ color: '#374151' }}>不動産売却履歴が見つかりません</h3>;
  }

  return (
    <div style={{
      color: '#374151',
    }}>
      <style>
        {`
          .property-details a {
            color: #0EA5E9 !important;
            text-decoration: underline;
          }
          .property-details a:hover {
            color: #38BDF8 !important;
          }
          .property-details p {
            color: #374151;
            margin: 0;
          }
        `}
      </style>
      <h2 style={{ marginBottom: 24, color: '#111827' }}>
        {propertySale?.building_name || "-"}
        {propertySale?.property_number ? `（${propertySale.property_number}）` : ""}
      </h2>
      {(goodReason || badReason || (simulationId && onReasonsUpdate)) && (
        <div style={{
          marginBottom: 24,
          padding: '16px 20px',
          background: '#F9FAFB',
          borderRadius: 8,
          border: '1px solid #E5E7EB',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ color: '#111827', margin: 0, fontSize: 15 }}>
              査定ポイント
            </h3>
            {simulationId && onReasonsUpdate && !editing && (
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={handleStartEdit}
                style={{ color: '#0EA5E9', fontSize: 13 }}
                size="small"
              >
                編集
              </Button>
            )}
          </div>
          {editing ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#16A34A', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                  GOOD
                </div>
                <TextArea
                  value={editGood}
                  onChange={(e) => setEditGood(e.target.value)}
                  placeholder="どこが良かったですか？"
                  rows={3}
                  style={{ background: '#FFFFFF', color: '#374151', borderColor: '#E5E7EB' }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#DC2626', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                  BAD
                </div>
                <TextArea
                  value={editBad}
                  onChange={(e) => setEditBad(e.target.value)}
                  placeholder="不安や懸念点はどこですか？"
                  rows={3}
                  style={{ background: '#FFFFFF', color: '#374151', borderColor: '#E5E7EB' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                  style={{ color: '#6B7280', borderColor: '#E5E7EB' }}
                >
                  キャンセル
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={handleSave}
                  loading={saving}
                >
                  保存
                </Button>
              </div>
            </>
          ) : (
            <>
              {goodReason && (
                <div style={{ marginBottom: badReason ? 14 : 0 }}>
                  <div style={{ color: '#16A34A', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                    GOOD
                  </div>
                  <div style={{ color: '#374151', fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {goodReason}
                  </div>
                </div>
              )}
              {badReason && (
                <div>
                  <div style={{ color: '#DC2626', fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
                    BAD
                  </div>
                  <div style={{ color: '#374151', fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {badReason}
                  </div>
                </div>
              )}
              {!goodReason && !badReason && (
                <div style={{ color: '#9CA3AF', fontSize: 13 }}>未入力</div>
              )}
            </>
          )}
        </div>
      )}

      <div className="property-details">
        <Descriptions
          column={1}
          bordered
          size='middle'
          layout='horizontal'
          labelStyle={{
            background: '#F9FAFB',
            color: '#6B7280',
            borderColor: '#E5E7EB'
          }}
          contentStyle={{
            background: '#FFFFFF',
            color: '#374151',
            borderColor: '#E5E7EB'
          }}
        >
        <Descriptions.Item label='物件番号'>
          {propertySale?.property_number || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='登録年月日'>
          {formatJapaneseDate(propertySale?.registered_at)}
        </Descriptions.Item>

        <Descriptions.Item label='変更年月日'>
          {formatJapaneseDate(propertySale?.changed_at)}
        </Descriptions.Item>

        <Descriptions.Item label='サイト更新日'>
          {formatJapaneseDate(propertySale?.updated_at_site)}
        </Descriptions.Item>

        <Descriptions.Item label='媒介契約年月日'>
          {formatJapaneseDate(propertySale?.contracted_at)}
        </Descriptions.Item>

        <Descriptions.Item label='物件種目'>
          {propertySale?.property_type}
        </Descriptions.Item>

        <Descriptions.Item label='取引態様'>
          {propertySale?.transaction_type || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='価格'>
          {propertySale?.price_total}
        </Descriptions.Item>

        <Descriptions.Item label='単価（㎡）'>
          {propertySale?.price_per_m2}
        </Descriptions.Item>

        <Descriptions.Item label='単価（坪）'>
          {propertySale?.price_per_tsubo}
        </Descriptions.Item>

        <Descriptions.Item label='面積（㎡）'>
          {propertySale?.area_m2}
        </Descriptions.Item>

        <Descriptions.Item label='面積（坪）'>
          {propertySale?.area_tubo ? (
            <p>{propertySale.area_tubo}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label='バルコニー面積'>
          {propertySale?.balcony_area_m2}
        </Descriptions.Item>

        <Descriptions.Item label='都道府県名'>
          {propertySale?.prefecture || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='所在地名１'>
          {propertySale?.address_1 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='所在地名２'>
          {propertySale?.address_2 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='所在地名３'>
          {propertySale?.address_3 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='物件名'>
          {propertySale?.building_name || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='部屋番号'>
          {propertySale?.room_number || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='沿線名１'>
          {propertySale?.line_1 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅名１'>
          {propertySale?.station_1 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅より徒歩１'>
          {propertySale?.walk_minutes_1}
        </Descriptions.Item>

        <Descriptions.Item label='沿線名２'>
          {propertySale?.line_2 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅名２'>
          {propertySale?.station_2 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅より徒歩２'>
          {propertySale?.walk_minutes_2}
        </Descriptions.Item>

        <Descriptions.Item label='沿線名３'>
          {propertySale?.line_3 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅名３'>
          {propertySale?.station_3 || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='駅より徒歩３'>
          {propertySale?.walk_minutes_3}
        </Descriptions.Item>

        <Descriptions.Item label='間取タイプ'>
          {propertySale?.layout_type || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='築年月'>
          {propertySale?.built_year ? (() => {
            const age = calcBuildingAge(propertySale.built_year.toString());
            if (age === null) return "-";
            return `${formatJapaneseDate(propertySale.built_year)} (築${age}年)`;
          })() : "-"}
        </Descriptions.Item>

        <Descriptions.Item label='建物構造'>
          {propertySale?.structure || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='地上階層'>
          {propertySale?.floor_count}
        </Descriptions.Item>

        <Descriptions.Item label='バルコニー方向'>
          {propertySale?.balcony_direction || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='管理組合有無'>
          {propertySale?.has_management_union !== null
            ? propertySale?.has_management_union
              ? "有"
              : "-"
            : "-"}
        </Descriptions.Item>

        <Descriptions.Item label='管理費'>
          {propertySale?.management_fee}
        </Descriptions.Item>

        <Descriptions.Item label='修繕積立金'>
          {propertySale?.reserve_fund}
        </Descriptions.Item>

        <Descriptions.Item label='管理形態'>
          {propertySale?.management_type || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='管理会社'>
          {propertySale?.management_company || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='施工会社名'>
          {propertySale?.construction_company || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='施主'>
          {propertySale?.developer || "-"}
        </Descriptions.Item>

        <Descriptions.Item label='備考１'>
          {propertySale?.note_1 ? (
            <p>{propertySale.note_1}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label='備考２'>
          {propertySale?.note_2 ? (
            <p>{propertySale.note_2}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label='備考３'>
          {propertySale?.note_3 ? (
            <p>{propertySale.note_3}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>

        <Descriptions.Item label='備考４'>
          {propertySale?.note_4 ? (
            <p>{propertySale.note_4}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='ステータス'>
          {propertySale?.status ? (
            <p>{propertySale.status}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='物件の詳細ページ'>
          {propertySale?.site_url ? (
            <p>{propertySale.site_url}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='掲載終了日'>
          {formatJapaneseDate(propertySale?.closed_at)}
        </Descriptions.Item>
        <Descriptions.Item label='掲載日時'>
          {propertySale?.post_date ? (
            <p>{propertySale.post_date}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='利回り'>
          {propertySale?.yield ? (
            <p>{propertySale.yield}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='駐車場在否'>
          {propertySale?.has_parking ? (
            <p>{propertySale.has_parking}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='駐車場月額'>
          {propertySale?.parking_fee ? (
            <p>{propertySale.parking_fee}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='土地権利'>
          {propertySale?.land_ownership ? (
            <p>{propertySale.land_ownership}</p>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label='間取り図'>
          {(() => {
            const driveUrl = buildDrivePreviewUrl(propertySale?.floor_plan_path);
            return driveUrl ? (
              <a
                href={driveUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                新しいタブで表示
              </a>
            ) : (
              "-"
            );
          })()}
        </Descriptions.Item>

        <Descriptions.Item label='作成日時'>
          {formatJapaneseDate(propertySale?.created_at)}
        </Descriptions.Item>

        <Descriptions.Item label='更新日時'>
          {formatJapaneseDate(propertySale?.updated_at)}
        </Descriptions.Item>
      </Descriptions>
      </div>
    </div>
  );
}
